import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Wrench, MapPin, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { turkeyData, cities } from '../data/turkeyData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UstaRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    password: '',
    password_confirm: '',
    tc_kimlik: '',
    city: '',
    district: '',
    address: '',
    service_categories: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const categories = [
    { id: 'kombi', label: 'Kombi Servisi' },
    { id: 'klima', label: 'Klima Servisi' },
    { id: 'beyaz_esya', label: 'Beyaz Eşya' },
    { id: 'elektrik', label: 'Elektrik Arızaları' },
    { id: 'su_tesisati', label: 'Su Tesisatı' },
    { id: 'petek', label: 'Petek Temizliği' },
    { id: 'camasir_makinesi', label: 'Çamaşır Makinesi' },
    { id: 'bulasik_makinesi', label: 'Bulaşık Makinesi' },
    { id: 'buzdolabi', label: 'Buzdolabı' }
  ];

  // Seçilen ilin ilçelerini getir
  const getDistricts = () => {
    if (!formData.city) return [];
    return turkeyData[formData.city] || [];
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      service_categories: prev.service_categories.includes(categoryId)
        ? prev.service_categories.filter(id => id !== categoryId)
        : [...prev.service_categories, categoryId]
    }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 0 && !value.startsWith('0')) {
      value = '0' + value;
    }
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    setFormData({...formData, phone_number: value});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.service_categories.length === 0) {
      setError('Lütfen en az bir servis kategorisi seçin');
      return;
    }

    if (!formData.city || !formData.district) {
      setError('Lütfen il ve ilçe seçin');
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (formData.password !== formData.password_confirm) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { password_confirm, ...submitData } = formData;
      await axios.post(`${API_URL}/api/usta/register`, submitData);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '));
      } else {
        setError('Kayıt oluşturulamadı. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Başarılı kayıt ekranı
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#EDF6F9] via-[#E0F2F4] to-[#B3E0E6] py-12 px-4 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Başvurunuz Alındı!</h2>
          <p className="text-gray-600 mb-6">
            Usta başvurunuz başarıyla oluşturuldu. Başvurunuz yönetici tarafından incelendikten sonra 
            size SMS ile bilgilendirme yapılacaktır.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Onay sürecinin ardından giriş yapabilir ve iş almaya başlayabilirsiniz.
          </p>
          <Button
            onClick={() => navigate('/prousta')}
            className="bg-[#006D77] hover:bg-[#005861] text-white rounded-full px-8 py-3"
          >
            Giriş Sayfasına Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EDF6F9] via-[#E0F2F4] to-[#B3E0E6] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#B3E0E6]/30">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-[#006D77] to-[#005861] rounded-full p-4">
              <Wrench className="w-12 h-12 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-heading font-bold text-[#264653] text-center mb-2">
            Usta Kayıt
          </h1>
          <p className="text-[#5F7D8B] text-center mb-8">
            HizmetGeliyo'ya katılın, müşterilere ulaşın
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="full_name">Adınız Soyadınız *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className="rounded-xl mt-2"
                required
                data-testid="usta-fullname"
              />
            </div>

            <div>
              <Label htmlFor="phone_number">Telefon Numaranız *</Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="05XX XXX XX XX"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                className="rounded-xl mt-2"
                required
                data-testid="usta-phone"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">Şifre *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="En az 6 karakter"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="rounded-xl mt-2"
                  required
                  data-testid="usta-password"
                />
              </div>
              <div>
                <Label htmlFor="password_confirm">Şifre Tekrar *</Label>
                <Input
                  id="password_confirm"
                  type="password"
                  placeholder="Şifreyi tekrar girin"
                  value={formData.password_confirm}
                  onChange={(e) => setFormData({...formData, password_confirm: e.target.value})}
                  className="rounded-xl mt-2"
                  required
                  data-testid="usta-password-confirm"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tc_kimlik">TC Kimlik No *</Label>
              <Input
                id="tc_kimlik"
                placeholder="XXXXXXXXXXX"
                value={formData.tc_kimlik}
                onChange={(e) => setFormData({...formData, tc_kimlik: e.target.value.replace(/\D/g, '').slice(0, 11)})}
                className="rounded-xl mt-2"
                maxLength={11}
                required
                data-testid="usta-tc"
              />
            </div>

            {/* İl ve İlçe Seçimi */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  İl *
                </Label>
                <select
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value, district: ''})}
                  className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006D77] bg-white"
                  required
                  data-testid="usta-city"
                >
                  <option value="">İl Seçin</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="district" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  İlçe *
                </Label>
                <select
                  id="district"
                  value={formData.district}
                  onChange={(e) => setFormData({...formData, district: e.target.value})}
                  className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006D77] bg-white disabled:bg-gray-100"
                  disabled={!formData.city}
                  required
                  data-testid="usta-district"
                >
                  <option value="">İlçe Seçin</option>
                  {getDistricts().map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="address">Adresiniz (Detay) *</Label>
              <Textarea
                id="address"
                placeholder="Mahalle, sokak, bina no gibi detayları yazın"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="rounded-xl mt-2 min-h-[80px]"
                required
                data-testid="usta-address"
              />
            </div>

            <div>
              <Label className="mb-3 block">Hizmet Verdiğiniz Kategoriler * (En az 1)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categories.map((category) => (
                  <div key={category.id} className="flex items-center gap-3 p-3 border border-[#B3E0E6] rounded-xl hover:bg-[#E0F2F4] transition-colors">
                    <Checkbox
                      id={category.id}
                      checked={formData.service_categories.includes(category.id)}
                      onCheckedChange={() => toggleCategory(category.id)}
                      data-testid={`category-${category.id}`}
                    />
                    <Label htmlFor={category.id} className="cursor-pointer flex-1 !mt-0">
                      {category.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
              <p className="font-medium mb-1">Önemli Bilgi:</p>
              <p>Kayıt işleminiz tamamlandıktan sonra başvurunuz yönetici onayına gönderilecektir. 
              Onay sonrası giriş yapabilir ve iş almaya başlayabilirsiniz.</p>
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                onClick={() => navigate('/prousta')}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Geri
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#006D77] hover:bg-[#005861] text-white rounded-full py-6 text-lg font-semibold"
                data-testid="usta-register-submit"
              >
                {loading ? 'Kaydediliyor...' : 'Kayıt Ol'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UstaRegister;
